// Per-site intent model client. Shapes mirror packages/server/src/routes/intents.ts:
// the site-admin router at /api/admin/sites/:siteId/intents and the per-
// conversation label at /api/conversations/:sessionId/intent.

import { apiFetch } from './api';

export interface IntentLabel {
  id: string;
  key: string;
  name: string;
  /** The "wants a human" label: a confident match hands the chat to an agent. */
  isHandoff: boolean;
  exampleCount: number;
}

export interface IntentModelStatus {
  version: number;
  /** Held-out accuracy at training time; null when too few examples to split. */
  accuracy: number | null;
  report: { testCount?: number; perLabel?: Record<string, { precision: number; recall: number; support: number }> };
  exampleCount: number;
  trainedAt: string;
}

export interface IntentSettings {
  handoffEnabled: boolean;
  handoffThreshold: number;
}

export interface IntentSummary {
  labels: IntentLabel[];
  model: IntentModelStatus | null;
  settings: IntentSettings;
}

export interface IntentExample {
  id: string;
  text: string;
  source: 'seed' | 'admin' | 'correction';
  createdAt: string;
}

export interface TrainResult {
  replaced: boolean;
  version: number | null;
  accuracy: number | null;
  previousAccuracy: number | null;
  exampleCount: number;
}

const base = (siteId: string) => `/api/admin/sites/${siteId}/intents`;

export const getIntents = (siteId: string) => apiFetch<IntentSummary>(base(siteId));

export const patchIntentSettings = (siteId: string, patch: Partial<IntentSettings>) =>
  apiFetch(`${base(siteId)}/settings`, { method: 'PATCH', body: JSON.stringify(patch) });

export const createIntentLabel = (siteId: string, input: { key: string; name: string; isHandoff?: boolean }) =>
  apiFetch(`${base(siteId)}/labels`, { method: 'POST', body: JSON.stringify(input) });

export const updateIntentLabel = (siteId: string, labelId: string, patch: { name?: string; isHandoff?: boolean }) =>
  apiFetch(`${base(siteId)}/labels/${labelId}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteIntentLabel = (siteId: string, labelId: string) =>
  apiFetch(`${base(siteId)}/labels/${labelId}`, { method: 'DELETE' });

export const listIntentExamples = (siteId: string, labelId: string) =>
  apiFetch<{ examples: IntentExample[] }>(`${base(siteId)}/labels/${labelId}/examples`).then((r) => r.examples);

export const addIntentExamples = (siteId: string, labelId: string, texts: string[]) =>
  apiFetch<{ added: number; duplicates: number }>(`${base(siteId)}/labels/${labelId}/examples`, {
    method: 'POST',
    body: JSON.stringify({ texts }),
  });

export const deleteIntentExample = (siteId: string, exampleId: string) =>
  apiFetch(`${base(siteId)}/examples/${exampleId}`, { method: 'DELETE' });

export const trainIntents = (siteId: string) => apiFetch<TrainResult>(`${base(siteId)}/train`, { method: 'POST' });

/** Top predictions for a typed message; 409 `no-model` until the site has trained. */
export const classifyIntent = (siteId: string, text: string) =>
  apiFetch<{ predictions: Array<{ label: string; p: number }> }>(`${base(siteId)}/classify`, {
    method: 'POST',
    body: JSON.stringify({ text }),
  }).then((r) => r.predictions);

// ─── per conversation ───

export interface ConversationIntent {
  key: string;
  /** null when an agent set it. */
  confidence: number | null;
  /** model = classified (live while the chat is open); agent = corrected by a person. */
  source: 'model' | 'agent' | null;
}

export const getConversationIntent = (sessionId: string) =>
  apiFetch<{ intent: ConversationIntent | null; labels: Array<{ key: string; name: string }> }>(
    `/api/conversations/${encodeURIComponent(sessionId)}/intent`,
  );

/** An agent's correction: re-tags the chat and teaches the model. */
export const setConversationIntent = (sessionId: string, key: string) =>
  apiFetch(`/api/conversations/${encodeURIComponent(sessionId)}/intent`, {
    method: 'PUT',
    body: JSON.stringify({ key }),
  });
